// ABOUTME: Software meldingen list view — ShDataGrid2 of all meldingen with publish/unpublish toggle.
// ABOUTME: Click a row opens MCreateMelding to edit; "Add" creates a new melding.

import { Box, IconButton, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import AddIcon from "@mui/icons-material/Add";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { tokens } from "../../../../theme";
import Shbutton from "../../../../components/theme/buttons/Shbutton";
import ButtonGroup from "../../../../components/theme/buttons/ButtonGroup";
import ShDataGrid2 from "../../../../components/theme/datagrid2/ShDataGrid2";
import ShLabel from "../../../../components/theme/label/ShLabel";
import ShTextSkeleton from "../../../../components/theme/skeleton/ShTextSkeleton";
import ShLabelSkeleton from "../../../../components/theme/skeleton/ShLabelSkeleton";
import Text from "../../../../components/theme/text/Text";
import MCreateMelding from "../../../../components/global/Modals/MCreateNotice/MCreateNotice";
import MConfirmDelete from "../../../../components/global/Modals/MConfirmDelete";
import MeldingTypeBadge from "../../../../components/global/Modals/MCreateNotice/MeldingTypeBadge";
import { findMeldingType } from "../../../../components/global/Modals/MCreateNotice/meldingTypes";
import {
    useDeleteNewsArticleMutation,
    useGetMeldingenListQuery,
    usePublishNewsArticleMutation,
    useResolveNewsArticleMutation,
    useUnpublishNewsArticleMutation,
} from "../../../../newapi/global/newsRootSlice";
import { showToastMessage, showToastMessageRequestError } from "../../../../utils/toasts";

const gridOptions = {
    export: false,
    disableCustomActionsOnPhone: true,
    isSidepanel: false,
};

const GridActions = ({ onAdd, t, theme }) => (
    <ButtonGroup version={2}>
        <Shbutton
            className="group-btn"
            color={theme.palette.mode === "dark" ? "blue" : "primary"}
            variant="contained"
            onClick={() => onAdd()}
            endIcon={<AddIcon />}
        >
            {t("create_melding") || "Melding aanmaken"}
        </Shbutton>
    </ButtonGroup>
);

const Meldingen = (props) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [createOpen, setCreateOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [publishArticle] = usePublishNewsArticleMutation();
    const [unpublishArticle] = useUnpublishNewsArticleMutation();
    const [resolveArticle] = useResolveNewsArticleMutation();
    const [deleteArticle] = useDeleteNewsArticleMutation();

    const openAdd = () => {
        setSelectedId(null);
        setCreateOpen(true);
    };

    useEffect(() => {
        props.replaceView({
            ...props.config,
            gridOptions,
            mobileActions: <GridActions onAdd={openAdd} t={t} theme={theme} />,
        });
    }, []);

    const openEdit = (params) => {
        if (!params?.row || params.row.skeleton) return;
        setSelectedId(params.row.id);
        setCreateOpen(true);
    };

    const handleClose = () => {
        setSelectedId(null);
        setCreateOpen(false);
    };

    const handleToggle = async (e, row) => {
        e.stopPropagation();
        const wasPublished = row.status === "published";
        try {
            if (wasPublished) {
                await unpublishArticle(row.id).unwrap();
                showToastMessage("success", t("melding_unpublished") || "Melding gedepubliceerd");
            } else {
                await publishArticle(row.id).unwrap();
                showToastMessage("success", t("melding_published") || "Melding gepubliceerd");
            }
        } catch (err) {
            showToastMessageRequestError(
                wasPublished ? (t("unpublish_failed") || "Depubliceren mislukt") : (t("publish_failed") || "Publiceren mislukt"),
                t,
                err,
            );
        }
    };

    const handleResolve = async (e, row) => {
        e.stopPropagation();
        try {
            await resolveArticle(row.id).unwrap();
            showToastMessage("success", t("melding_resolved") || "Melding opgelost");
        } catch (err) {
            showToastMessageRequestError(t("resolve_failed") || "Oplossen mislukt", t, err);
        }
    };

    const handleDelete = (e, row) => {
        e.stopPropagation();
        setDeleteTarget(row);
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            await deleteArticle(deleteTarget.id).unwrap();
            showToastMessage("success", t("melding_deleted") || "Melding verwijderd");
        } catch (err) {
            showToastMessageRequestError(t("delete_failed") || "Verwijderen mislukt", t, err);
        }
    };

    const columns = [
        {
            field: "type",
            headerName: t("type"),
            flex: 1,
            renderCell: (params) => {
                if (params.row.skeleton) return <ShTextSkeleton />;
                const entry = findMeldingType(params.row.type);
                if (!entry) return <Text>-</Text>;
                return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <MeldingTypeBadge typeId={params.row.type} size={28} />
                        <Text>{entry.label}</Text>
                    </Box>
                );
            },
        },
        {
            field: "title",
            headerName: t("title"),
            flex: 2,
            renderCell: (params) => {
                if (params.row.skeleton) return <ShTextSkeleton />;
                return <Text>{params.row.title}</Text>;
            },
        },
        {
            field: "status",
            headerName: t("status"),
            flex: 1,
            renderCell: (params) => {
                if (params.row.skeleton) return <ShLabelSkeleton />;
                const isPublished = params.row.status === "published";
                const isResolved = isPublished && params.row.resolvedAt;
                if (isResolved) {
                    return (
                        <ShLabel size={32} palette="normal" variant="green">
                            {t("resolved") || "Opgelost"}
                        </ShLabel>
                    );
                }
                return (
                    <ShLabel size={32} palette="normal" variant={isPublished ? theme.palette.mode === "dark" ? "blue" : "primary" : theme.palette.mode === "dark" ? "greyLight" : "grey"}>
                        {isPublished ? t("active") || "Actief" : t("draft") || "Concept"}
                    </ShLabel>
                );
            },
        },
        {
            field: "audience",
            headerName: t("audience"),
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                if (params.row.skeleton) return <ShTextSkeleton />;
                const audiences = params.row.audience ?? [];
                if (audiences.length === 0) return <Text>-</Text>;
                return <Text>{audiences.map((a) => t(a)).join(", ")}</Text>;
            },
        },
        {
            field: "publishedAt",
            headerName: t("published_at") || t("date"),
            flex: 1,
            shFilter: { type: "date" },
            renderCell: (params) => {
                if (params.row.skeleton) return <ShTextSkeleton />;
                const raw = params.row.publishedAt || params.row.createdAt;
                if (!raw) return <Text>-</Text>;
                const d = new Date(raw);
                return <Text>{d.toLocaleDateString(i18n.language)}</Text>;
            },
        },
        {
            field: "actions",
            headerName: "",
            width: 144,
            sortable: false,
            renderCell: (params) => {
                if (params.row.skeleton) return null;
                const isPublished = params.row.status === "published";
                const isResolved = isPublished && params.row.resolvedAt;
                return (
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                        {isPublished && !isResolved && (
                            <IconButton
                                size="small"
                                onClick={(e) => handleResolve(e, params.row)}
                                title={t("resolve") || "Oplossen"}
                                sx={{ color: colors.green[400] }}
                            >
                                <TaskAltOutlinedIcon />
                            </IconButton>
                        )}
                        <IconButton
                            size="small"
                            onClick={(e) => handleToggle(e, params.row)}
                            title={isPublished ? (t("unpublish") || "Depubliceren") : (t("publish") || "Publiceren")}
                            sx={{ color: isPublished ? theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400] : colors.txt["secondary"] }}
                        >
                            {isPublished ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={(e) => handleDelete(e, params.row)}
                            title={t("delete") || "Verwijderen"}
                            sx={{ color: colors.txt["secondary"], "&:hover": { color: colors.red[400] } }}
                        >
                            <DeleteOutlineIcon />
                        </IconButton>
                    </Box>
                );
            },
        },
    ];

    return (
        <Box height={1} display="flex" flexDirection="column">

            <ShDataGrid2
                key={theme.palette.mode}
                content={useGetMeldingenListQuery}
                gridActions={<GridActions onAdd={openAdd} t={t} theme={theme} />}
                title={t("meldingen") || "Meldingen"}
                gridOptions={gridOptions}
                sortModel={{ field: "createdAt", type: "DESC" }}
                columns={columns}
                onRowClick={openEdit}
            />

            <MCreateMelding
                open={createOpen}
                handleClose={handleClose}
                articleId={selectedId}
            />

            <MConfirmDelete
                open={Boolean(deleteTarget)}
                handleClose={() => setDeleteTarget(null)}
                confirm={confirmDelete}
                data={deleteTarget?.body}
                content={t("confirm_delete_melding")}
            />
        </Box>
    );
};

export default Meldingen;
